import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { leaveService } from '../services/leaveService';
import { userService } from '../services/userService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Plus,
  Search,
  User as UserIcon,
  Building2,
  PlayCircle,
  CalendarDays,
  Sliders,
  Send,
} from 'lucide-react';

const Leaves = () => {
  const { user } = useAuth();

  // Active Org and User state
  const [orgId, setOrgId] = useState(user?.orgId || user?.org_id || 1);
  const [selectedUserId, setSelectedUserId] = useState(user?.id || user?.userId || 5);
  const [employeeList, setEmployeeList] = useState([]);

  // Data states
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employeeLeaveCounts, setEmployeeLeaveCounts] = useState([]);
  const [appliedLeaves, setAppliedLeaves] = useState([]);
  const [orgLeaveCounts, setOrgLeaveCounts] = useState({});

  // Loading and Error states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Filter state for applied leaves
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterSearch, setFilterSearch] = useState('');

  // Apply Leave form state
  const [applyForm, setApplyForm] = useState({
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
    appliedLeave: 1,
    status: 'applied',
    reason: '',
  });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyError, setApplyError] = useState('');

  // Org Leave Type Form
  const [newTypeName, setNewTypeName] = useState('');
  const [createTypeError, setCreateTypeError] = useState('');
  const [createTypeLoading, setCreateTypeLoading] = useState(false);

  // Set Quota Modal state (Org or Employee)
  const [quotaModal, setQuotaModal] = useState({
    isOpen: false,
    mode: 'ORG', // 'ORG' or 'EMPLOYEE'
    leaveTypeId: '',
    leaveTypeName: '',
    currentCount: 0,
    newCount: 0,
    targetUserId: null,
  });
  const [quotaSubmitting, setQuotaSubmitting] = useState(false);
  const [quotaError, setQuotaError] = useState('');

  // QA Runner state
  const [qaLogs, setQaLogs] = useState([]);
  const [qaRunning, setQaRunning] = useState(false);

  // Auto-dismiss banners
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Load employee directory for org
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const users = await userService.getAll();
        if (Array.isArray(users) && users.length > 0) {
          const formatted = users.map((u) => ({
            id: u.id,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || `User #${u.id}`,
            email: u.email,
            orgId: u.orgId || u.org_id || 1,
          }));
          setEmployeeList(formatted);
        } else {
          setEmployeeList([
            { id: 5, name: 'Rajesh Kumar (User #5)', email: 'rajesh.kumar@company.com', orgId: 1 },
            { id: 101, name: 'Dev Employee (User #101)', email: 'employee@techapurv.com', orgId: 1 },
            { id: 102, name: 'Super Admin (User #102)', email: 'superadmin@techapurv.com', orgId: 1 },
          ]);
        }
      } catch (e) {
        setEmployeeList([
          { id: 5, name: 'Rajesh Kumar (User #5)', email: 'rajesh.kumar@company.com', orgId: 1 },
          { id: 101, name: 'Dev Employee (User #101)', email: 'employee@techapurv.com', orgId: 1 },
          { id: 102, name: 'Super Admin (User #102)', email: 'superadmin@techapurv.com', orgId: 1 },
        ]);
      }
    };
    loadEmployees();
  }, []);

  // Fetch all core leave data for the selected org and user
  const fetchLeaveData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    setRefreshing(true);
    setErrorMessage('');

    try {
      // 1. Get Org Leave Types: GET /{orgId}/leave-types
      const types = await leaveService.getLeaveTypes(orgId);
      setLeaveTypes(Array.isArray(types) ? types : []);

      // 2. Get All Leave Counts for Employee: GET /{orgId}/{userId}
      const employeeCounts = await leaveService.getEmployeeAllLeaveCounts(orgId, selectedUserId);
      setEmployeeLeaveCounts(Array.isArray(employeeCounts) ? employeeCounts : []);

      // 3. Get Applied Leaves: GET /{orgId}/{userId}/leave/
      const applied = await leaveService.getAppliedLeaves(orgId, selectedUserId, {
        fromDate: filterFromDate || undefined,
        toDate: filterToDate || undefined,
      });
      setAppliedLeaves(Array.isArray(applied) ? applied : []);

      // 4. Fetch Org-Level leave counts for each leave type: GET /{orgId}/{leaveTypeId}
      if (Array.isArray(types) && types.length > 0) {
        const countsMap = {};
        await Promise.all(
          types.map(async (t) => {
            try {
              const res = await leaveService.getOrgLeaveCount(orgId, t.leave_type_id);
              countsMap[t.leave_type_id] = res?.leave_count ?? 0;
            } catch (err) {
              countsMap[t.leave_type_id] = 0;
            }
          })
        );
        setOrgLeaveCounts(countsMap);
      }

      // Default applyForm leaveTypeId if not set
      if (types.length > 0 && !applyForm.leaveTypeId) {
        setApplyForm((prev) => ({ ...prev, leaveTypeId: String(types[0].leave_type_id) }));
      }
    } catch (err) {
      setErrorMessage(err.userMessage || 'Failed to load leave management data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orgId, selectedUserId, filterFromDate, filterToDate, applyForm.leaveTypeId]);

  useEffect(() => {
    fetchLeaveData();
  }, [fetchLeaveData]);

  // Calculate used leave count per leave type from applied leaves (APPLIED, APPROVED, ON-HOLD consume quota)
  const leaveBalances = useMemo(() => {
    return employeeLeaveCounts.map((countItem) => {
      const typeId = countItem.leave_type_id;
      const entitled = Number(countItem.total_leave_count || 0);

      // Total consumed leaves where status is in [APPLIED, APPROVED, ON-HOLD]
      const used = appliedLeaves
        .filter(
          (l) =>
            Number(l.leave_type_id) === Number(typeId) &&
            ['APPLIED', 'APPROVED', 'ON-HOLD'].includes((l.status || '').toUpperCase())
        )
        .reduce((sum, l) => sum + Number(l.applied_leave || 0), 0);

      const remaining = Math.max(0, entitled - used);
      const orgDefault = orgLeaveCounts[typeId] ?? 0;

      return {
        ...countItem,
        entitled,
        used,
        remaining,
        orgDefault,
      };
    });
  }, [employeeLeaveCounts, appliedLeaves, orgLeaveCounts]);

  // Selected leave type balance in Apply form
  const currentApplyTypeBalance = useMemo(() => {
    if (!applyForm.leaveTypeId) return null;
    return leaveBalances.find((b) => Number(b.leave_type_id) === Number(applyForm.leaveTypeId)) || null;
  }, [leaveBalances, applyForm.leaveTypeId]);

  // Auto calculate applied days when dates change
  const handleDateChange = (field, value) => {
    setApplyForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (updated.fromDate && updated.toDate) {
        const start = new Date(updated.fromDate);
        const end = new Date(updated.toDate);
        if (end >= start) {
          const diffTime = Math.abs(end - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          updated.appliedLeave = diffDays;
        }
      }
      return updated;
    });
  };

  // Submit Apply Leave
  const handleApplyLeave = async (e) => {
    e.preventDefault();
    setApplyError('');
    setApplySubmitting(true);

    try {
      const payload = {
        from_date: applyForm.fromDate,
        to_date: applyForm.toDate,
        leave_type_id: Number(applyForm.leaveTypeId),
        applied_leave: Number(applyForm.appliedLeave),
        status: applyForm.status,
      };

      const result = await leaveService.applyLeave(orgId, selectedUserId, payload);
      setSuccessMessage(`Leave successfully submitted! Application ID #${result.leave_id} (${result.status})`);

      // Reset form
      setApplyForm((prev) => ({
        ...prev,
        fromDate: '',
        toDate: '',
        appliedLeave: 1,
        reason: '',
      }));

      // Refresh data
      await fetchLeaveData(true);
      setActiveTab('overview');
    } catch (err) {
      setApplyError(err.userMessage || 'Failed to submit leave application.');
    } finally {
      setApplySubmitting(false);
    }
  };

  // Create Org Leave Type
  const handleCreateLeaveType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) return;

    setCreateTypeError('');
    setCreateTypeLoading(true);

    try {
      const result = await leaveService.createLeaveType(orgId, newTypeName.trim());
      setSuccessMessage(`Created leave type "${result.leave_type}" (ID: ${result.leave_type_id})`);
      setNewTypeName('');
      await fetchLeaveData(true);
    } catch (err) {
      setCreateTypeError(err.userMessage || 'Failed to create leave type.');
    } finally {
      setCreateTypeLoading(false);
    }
  };

  // Save Quota (Org or Employee)
  const handleSaveQuota = async () => {
    setQuotaError('');
    setQuotaSubmitting(true);

    try {
      if (quotaModal.mode === 'ORG') {
        // Set Org Leave Count: POST /{orgId}/{leaveTypeId}
        const updated = await leaveService.setOrgLeaveCount(
          orgId,
          quotaModal.leaveTypeId,
          quotaModal.newCount
        );
        setSuccessMessage(`Org default leave count for "${updated.leave_type}" updated to ${updated.leave_count} days.`);
      } else {
        // Set Employee Leave Count: POST /{orgId}/{userId}/{leaveTypeId}
        const updated = await leaveService.setEmployeeLeaveCount(
          orgId,
          quotaModal.targetUserId || selectedUserId,
          quotaModal.leaveTypeId,
          quotaModal.newCount
        );
        setSuccessMessage(`Employee quota for "${updated.leave_type}" updated to ${updated.total_leave_count} days.`);
      }

      setQuotaModal((prev) => ({ ...prev, isOpen: false }));
      await fetchLeaveData(true);
    } catch (err) {
      setQuotaError(err.userMessage || 'Failed to update quota.');
    } finally {
      setQuotaSubmitting(false);
    }
  };

  // Open Quota Modal
  const openSetQuota = (mode, item) => {
    setQuotaError('');
    setQuotaModal({
      isOpen: true,
      mode,
      leaveTypeId: item.leave_type_id,
      leaveTypeName: item.leave_type,
      currentCount: mode === 'ORG' ? (orgLeaveCounts[item.leave_type_id] ?? 0) : item.total_leave_count ?? 0,
      newCount: mode === 'ORG' ? (orgLeaveCounts[item.leave_type_id] ?? 0) : item.total_leave_count ?? 0,
      targetUserId: selectedUserId,
    });
  };

  // Filter applied leaves list
  const filteredAppliedLeaves = useMemo(() => {
    return appliedLeaves.filter((leave) => {
      if (filterStatus !== 'ALL' && (leave.status || '').toUpperCase() !== filterStatus) {
        return false;
      }
      if (filterSearch.trim()) {
        const q = filterSearch.trim().toLowerCase();
        const matchesType = (leave.leave_type || '').toLowerCase().includes(q);
        const matchesId = String(leave.leave_id).includes(q);
        if (!matchesType && !matchesId) return false;
      }
      return true;
    });
  }, [appliedLeaves, filterStatus, filterSearch]);

  // Status badge styling helper
  const renderStatusBadge = (status = '') => {
    const s = status.toUpperCase();
    switch (s) {
      case 'APPROVED':
        return <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'APPLIED':
        return <Badge className="bg-blue-600 hover:bg-blue-700 text-white"><Clock className="w-3 h-3 mr-1" /> Applied</Badge>;
      case 'ON-HOLD':
        return <Badge className="bg-amber-600 hover:bg-amber-700 text-white"><AlertTriangle className="w-3 h-3 mr-1" /> On-Hold</Badge>;
      case 'REJECTED':
        return <Badge className="bg-rose-600 hover:bg-rose-700 text-white"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Quick preset dates for applied leaves filter
  const applyPresetDate = (preset) => {
    const now = new Date();
    const year = now.getFullYear();
    const pad = (n) => String(n).padStart(2, '0');

    if (preset === 'THIS_YEAR') {
      setFilterFromDate('');
      setFilterToDate('');
    } else if (preset === 'LAST_30') {
      const past = new Date();
      past.setDate(past.getDate() - 30);
      setFilterFromDate(`${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(past.getDate())}`);
      setFilterToDate(`${year}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
    } else if (preset === 'NEXT_30') {
      const future = new Date();
      future.setDate(future.getDate() + 30);
      setFilterFromDate(`${year}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
      setFilterToDate(`${future.getFullYear()}-${pad(future.getMonth() + 1)}-${pad(future.getDate())}`);
    } else if (preset === 'CLEAR') {
      setFilterFromDate('');
      setFilterToDate('');
      setFilterStatus('ALL');
      setFilterSearch('');
    }
  };

  // Run the 15-Step QA Flow
  const runQaFlow = async () => {
    setQaRunning(true);
    setQaLogs([]);
    const logs = [];

    const addLog = (stepNumber, title, status, details, payload = null) => {
      const entry = { stepNumber, title, status, details, payload, time: new Date().toLocaleTimeString() };
      logs.push(entry);
      setQaLogs([...logs]);
    };

    try {
      const testOrgId = Number(orgId) || 1;
      const testUserId = Number(selectedUserId) || 5;

      // Step 1: Create org leave type
      let createdType = null;
      try {
        const typeName = `QA Leave ${Date.now() % 1000}`;
        createdType = await leaveService.createLeaveType(testOrgId, typeName);
        addLog(1, 'Create org leave type', 'PASS', `Created leave type "${typeName}" with ID ${createdType.leave_type_id}`, createdType);
      } catch (e) {
        addLog(1, 'Create org leave type', 'FAIL', e.userMessage || e.message);
      }

      // Step 2: Get org leave types
      let allTypes = [];
      try {
        allTypes = await leaveService.getLeaveTypes(testOrgId);
        addLog(2, 'Get org leave types', 'PASS', `Retrieved ${allTypes.length} leave types for Org ${testOrgId}`, allTypes);
      } catch (e) {
        addLog(2, 'Get org leave types', 'FAIL', e.userMessage || e.message);
      }

      const activeTypeId = createdType?.leave_type_id || allTypes[0]?.leave_type_id || 1;

      // Step 3: Set org leave count for leave type
      try {
        const orgCountRes = await leaveService.setOrgLeaveCount(testOrgId, activeTypeId, 12);
        addLog(3, 'Set org leave count', 'PASS', `Set default quota for type ${activeTypeId} to 12 days`, orgCountRes);
      } catch (e) {
        addLog(3, 'Set org leave count', 'FAIL', e.userMessage || e.message);
      }

      // Step 4: Get org leave count
      try {
        const getOrgRes = await leaveService.getOrgLeaveCount(testOrgId, activeTypeId);
        addLog(4, 'Get org leave count', 'PASS', `Retrieved org count: ${getOrgRes.leave_count} days`, getOrgRes);
      } catch (e) {
        addLog(4, 'Get org leave count', 'FAIL', e.userMessage || e.message);
      }

      // Step 5: Set employee leave count
      try {
        const empCountRes = await leaveService.setEmployeeLeaveCount(testOrgId, testUserId, activeTypeId, 10);
        addLog(5, 'Set employee leave count', 'PASS', `Set employee ${testUserId} entitlement to 10 days`, empCountRes);
      } catch (e) {
        addLog(5, 'Set employee leave count', 'FAIL', e.userMessage || e.message);
      }

      // Step 6: Get employee leave count
      try {
        const getEmpRes = await leaveService.getEmployeeLeaveCount(testOrgId, testUserId, activeTypeId);
        addLog(6, 'Get employee leave count', 'PASS', `Verified employee entitlement is ${getEmpRes.total_leave_count} days`, getEmpRes);
      } catch (e) {
        addLog(6, 'Get employee leave count', 'FAIL', e.userMessage || e.message);
      }

      // Step 7: Get all employee leave counts
      try {
        const getAllEmpRes = await leaveService.getEmployeeAllLeaveCounts(testOrgId, testUserId);
        addLog(7, 'Get all employee leave counts', 'PASS', `Retrieved ${getAllEmpRes.length} configured leave types for employee ${testUserId}`, getAllEmpRes);
      } catch (e) {
        addLog(7, 'Get all employee leave counts', 'FAIL', e.userMessage || e.message);
      }

      // Step 8: Apply leave with status 'applied'
      try {
        const applyRes = await leaveService.applyLeave(testOrgId, testUserId, {
          from_date: '2026-10-01',
          to_date: '2026-10-03',
          leave_type_id: activeTypeId,
          applied_leave: 3,
          status: 'applied',
        });
        addLog(8, 'Apply leave with status applied', 'PASS', `Applied 3 days, application #${applyRes.leave_id} created`, applyRes);
      } catch (e) {
        addLog(8, 'Apply leave with status applied', 'FAIL', e.userMessage || e.message);
      }

      // Step 9: Apply leave with status 'approved'
      try {
        const approveRes = await leaveService.applyLeave(testOrgId, testUserId, {
          from_date: '2026-10-04',
          to_date: '2026-10-05',
          leave_type_id: activeTypeId,
          applied_leave: 2,
          status: 'approved',
        });
        addLog(9, 'Apply leave with status approved', 'PASS', `Approved 2 days, application #${approveRes.leave_id} created`, approveRes);
      } catch (e) {
        addLog(9, 'Apply leave with status approved', 'FAIL', e.userMessage || e.message);
      }

      // Step 10: Apply leave with status 'on-hold'
      try {
        const holdRes = await leaveService.applyLeave(testOrgId, testUserId, {
          from_date: '2026-10-06',
          to_date: '2026-10-06',
          leave_type_id: activeTypeId,
          applied_leave: 1,
          status: 'on-hold',
        });
        addLog(10, 'Apply leave with status on-hold', 'PASS', `On-hold 1 day, application #${holdRes.leave_id} created`, holdRes);
      } catch (e) {
        addLog(10, 'Apply leave with status on-hold', 'FAIL', e.userMessage || e.message);
      }

      // Step 11: Apply leave with status 'rejected'
      try {
        const rejectRes = await leaveService.applyLeave(testOrgId, testUserId, {
          from_date: '2026-10-07',
          to_date: '2026-10-07',
          leave_type_id: activeTypeId,
          applied_leave: 1,
          status: 'rejected',
        });
        addLog(11, 'Apply leave with status rejected', 'PASS', `Rejected 1 day (does not consume entitlement), application #${rejectRes.leave_id}`, rejectRes);
      } catch (e) {
        addLog(11, 'Apply leave with status rejected', 'FAIL', e.userMessage || e.message);
      }

      // Step 12: Verify entitlement overflow returns 409 Conflict
      try {
        // Attempt to apply 100 days which exceeds the 10 days quota
        await leaveService.applyLeave(testOrgId, testUserId, {
          from_date: '2026-10-10',
          to_date: '2026-10-20',
          leave_type_id: activeTypeId,
          applied_leave: 50,
          status: 'applied',
        });
        addLog(12, 'Verify entitlement overflow (409 Conflict)', 'FAIL', 'Expected 409 Conflict error but call succeeded');
      } catch (err) {
        const status = err.response?.status;
        if (status === 409 || err.message?.includes('409') || err.userMessage?.includes('409')) {
          addLog(12, 'Verify entitlement overflow (409 Conflict)', 'PASS', `Correctly rejected with 409 Conflict: "${err.userMessage || err.message}"`, err.response?.data);
        } else {
          addLog(12, 'Verify entitlement overflow (409 Conflict)', 'FAIL', `Expected 409 Conflict but received status ${status || 'unknown'}: ${err.message}`);
        }
      }

      // Step 13: Get applied leaves with no date range (Default Jan 1 - Dec 31)
      try {
        const defaultLeaves = await leaveService.getAppliedLeaves(testOrgId, testUserId);
        addLog(13, 'Get applied leaves with no date range', 'PASS', `Default filter returned ${defaultLeaves.length} leaves for current year`, defaultLeaves);
      } catch (e) {
        addLog(13, 'Get applied leaves with no date range', 'FAIL', e.userMessage || e.message);
      }

      // Step 14: Get applied leaves with explicit date range
      try {
        const filteredLeaves = await leaveService.getAppliedLeaves(testOrgId, testUserId, {
          fromDate: '2026-10-01',
          toDate: '2026-10-31',
        });
        addLog(14, 'Get applied leaves with explicit date range', 'PASS', `Explicit range returned ${filteredLeaves.length} leaves`, filteredLeaves);
      } catch (e) {
        addLog(14, 'Get applied leaves with explicit date range', 'FAIL', e.userMessage || e.message);
      }

      // Step 15: Verify bad date range returns 400 Bad Request
      try {
        // toDate earlier than fromDate
        await leaveService.getAppliedLeaves(testOrgId, testUserId, {
          fromDate: '2026-12-31',
          toDate: '2026-01-01',
        });
        addLog(15, 'Verify bad date range (400 Bad Request)', 'FAIL', 'Expected 400 Bad Request but call succeeded');
      } catch (err) {
        const status = err.response?.status;
        if (status === 400 || err.message?.includes('400') || err.userMessage?.includes('400')) {
          addLog(15, 'Verify bad date range (400 Bad Request)', 'PASS', `Correctly rejected with 400 Bad Request: "${err.userMessage || err.message}"`, err.response?.data);
        } else {
          addLog(15, 'Verify bad date range (400 Bad Request)', 'FAIL', `Expected 400 Bad Request but received: ${err.message}`);
        }
      }

      setSuccessMessage('QA Flow completed! See logs below for test results.');
      await fetchLeaveData(true);
    } catch (criticalErr) {
      setErrorMessage(`QA Flow interrupted: ${criticalErr.message}`);
    } finally {
      setQaRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Alerts */}
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg flex items-center justify-between shadow-sm animate-in fade-in">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-emerald-600 hover:text-emerald-800 text-sm font-bold">×</button>
        </div>
      )}

      {errorMessage && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-lg flex items-center justify-between shadow-sm animate-in fade-in">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0" />
            <span className="text-sm font-medium">{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage('')} className="text-rose-600 hover:text-rose-800 text-sm font-bold">×</button>
        </div>
      )}

      {/* Main Header & Scope Switcher */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 rounded-2xl shadow-lg border border-slate-800">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold tracking-tight">Leave Management</h1>
            <Badge className="bg-indigo-500/30 text-indigo-200 border-indigo-400/40">API Spec v1.0</Badge>
          </div>
          <p className="text-slate-300 mt-1 text-sm">
            Track entitlements, manage organization quotas, and process leave requests with date validation.
          </p>
        </div>

        {/* Global Controls: Org, User, Refresh */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center bg-slate-800/80 rounded-lg px-3 py-1.5 border border-slate-700 text-sm">
            <Building2 className="w-4 h-4 text-indigo-400 mr-2" />
            <span className="text-slate-400 text-xs mr-2">Org ID:</span>
            <input
              type="number"
              min="1"
              value={orgId}
              onChange={(e) => setOrgId(Number(e.target.value) || 1)}
              className="w-14 bg-slate-900 text-white text-center font-semibold rounded border border-slate-600 px-1 py-0.5 text-xs focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-center bg-slate-800/80 rounded-lg px-3 py-1.5 border border-slate-700 text-sm">
            <UserIcon className="w-4 h-4 text-emerald-400 mr-2" />
            <span className="text-slate-400 text-xs mr-2">Employee:</span>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(Number(e.target.value))}
              className="bg-slate-900 text-white text-xs rounded border border-slate-600 px-2 py-1 font-medium focus:ring-1 focus:ring-emerald-500"
            >
              {employeeList.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} (ID: {emp.id})
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchLeaveData()}
            disabled={refreshing}
            className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          <Button
            size="sm"
            onClick={() => setActiveTab('apply')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-md shadow-indigo-600/30"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Apply Leave
          </Button>
        </div>
      </div>

      {/* Leave Balance Cards Overview */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <CalendarDays className="w-5 h-5 mr-2 text-indigo-600" />
            Employee Leave Entitlements & Balances
          </h2>
          <span className="text-xs text-gray-500">
            For Employee #{selectedUserId} &bull; Used leaves include Applied, Approved, and On-Hold
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse bg-gray-50 border-gray-200">
                <CardHeader className="h-16 bg-gray-100 rounded-t-lg" />
                <CardContent className="h-28" />
              </Card>
            ))}
          </div>
        ) : leaveBalances.length === 0 ? (
          <Card className="border-dashed p-6 text-center text-gray-500">
            <Calendar className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="font-medium">No leave types configured for this employee in Org {orgId}.</p>
            <p className="text-sm mt-1">Configure leave quotas below in the Org Policies tab.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {leaveBalances.map((item) => {
              const percentUsed = item.entitled > 0 ? Math.min(100, Math.round((item.used / item.entitled) * 100)) : 0;
              const isOverQuota = item.used > item.entitled;

              return (
                <Card key={item.leave_type_id} className="relative overflow-hidden border-slate-200 hover:shadow-md transition-shadow">
                  <div
                    className={`h-1.5 w-full ${
                      item.remaining === 0 ? 'bg-rose-500' : item.remaining <= 3 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                  />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-base">{item.leave_type}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Type ID: #{item.leave_type_id}</p>
                      </div>
                      <Badge variant="outline" className="text-xs bg-slate-50 font-normal">
                        Org Def: {item.orgDefault}d
                      </Badge>
                    </div>

                    <div className="mt-4 flex items-baseline justify-between">
                      <div>
                        <span className="text-3xl font-extrabold text-gray-900">{item.remaining}</span>
                        <span className="text-xs font-medium text-gray-500 ml-1.5">days left</span>
                      </div>
                      <div className="text-right text-xs text-gray-600">
                        <span className="font-semibold text-gray-900">{item.used}</span> / {item.entitled} used
                      </div>
                    </div>

                    {/* Progress indicator */}
                    <div className="mt-3 w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          isOverQuota
                            ? 'bg-rose-600'
                            : percentUsed > 80
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                        }`}
                        style={{ width: `${percentUsed}%` }}
                      />
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                      <span className="text-gray-500">
                        Entitlement: <strong className="text-gray-800">{item.entitled} days</strong>
                      </span>
                      <button
                        onClick={() => openSetQuota('EMPLOYEE', item)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
                      >
                        <Sliders className="w-3 h-3 mr-1" /> Adjust
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Tabs Container */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl border border-slate-200">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Clock className="w-4 h-4 mr-2 text-indigo-600" />
            Applied Leaves ({appliedLeaves.length})
          </TabsTrigger>
          <TabsTrigger value="apply" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Send className="w-4 h-4 mr-2 text-emerald-600" />
            Apply Leave
          </TabsTrigger>
          <TabsTrigger value="org-policies" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Building2 className="w-4 h-4 mr-2 text-blue-600" />
            Org Leave Types & Quotas
          </TabsTrigger>
          <TabsTrigger value="employee-entitlements" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Sliders className="w-4 h-4 mr-2 text-purple-600" />
            Employee Quotas
          </TabsTrigger>
          <TabsTrigger value="qa-tester" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <PlayCircle className="w-4 h-4 mr-2 text-rose-600" />
            QA Flow & API Tester (15 Steps)
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: APPLIED LEAVES HISTORY */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">Applied Leaves History</CardTitle>
                  <CardDescription>
                    Endpoint: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-indigo-600">GET /{orgId}/{selectedUserId}/leave/</code>
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyPresetDate('THIS_YEAR')}
                    className={`text-xs ${!filterFromDate && !filterToDate ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : ''}`}
                  >
                    Current Year (Default)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyPresetDate('LAST_30')}
                    className="text-xs"
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyPresetDate('NEXT_30')}
                    className="text-xs"
                  >
                    Next 30 Days
                  </Button>
                  {(filterFromDate || filterToDate || filterStatus !== 'ALL' || filterSearch) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => applyPresetDate('CLEAR')}
                      className="text-xs text-rose-600 hover:text-rose-700"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Filter controls */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-4 border-t border-gray-100">
                <div>
                  <Label htmlFor="filterFrom" className="text-xs text-gray-500">From Date</Label>
                  <Input
                    id="filterFrom"
                    type="date"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    className="h-9 mt-1 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="filterTo" className="text-xs text-gray-500">To Date</Label>
                  <Input
                    id="filterTo"
                    type="date"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    className="h-9 mt-1 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="filterStatus" className="text-xs text-gray-500">Status</Label>
                  <select
                    id="filterStatus"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="h-9 mt-1 w-full rounded-md border border-gray-300 px-3 text-xs bg-white"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="APPLIED">Applied</option>
                    <option value="APPROVED">Approved</option>
                    <option value="ON-HOLD">On-Hold</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="filterSearch" className="text-xs text-gray-500">Search</Label>
                  <div className="relative mt-1">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                    <Input
                      id="filterSearch"
                      placeholder="Search leave type or ID..."
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      className="h-9 pl-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="border rounded-xl overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-20">ID</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>From Date</TableHead>
                      <TableHead>To Date</TableHead>
                      <TableHead className="text-center">Days</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAppliedLeaves.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          <Clock className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                          <p className="font-medium">No applied leaves found for this criteria.</p>
                          <p className="text-xs mt-1 text-gray-400">
                            Apply for a new leave or adjust the date filter above.
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAppliedLeaves.map((item) => (
                        <TableRow key={item.leave_id} className="hover:bg-slate-50/70">
                          <TableCell className="font-mono text-xs font-semibold text-slate-700">
                            #{item.leave_id}
                          </TableCell>
                          <TableCell className="font-medium text-gray-900">
                            {item.leave_type || `Type #${item.leave_type_id}`}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-600">
                            {item.from_date}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-600">
                            {item.to_date}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-800">
                              {item.applied_leave} {item.applied_leave === 1 ? 'day' : 'days'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {renderStatusBadge(item.status)}
                          </TableCell>
                          <TableCell className="text-right">
                            {/* Action to update or change status */}
                            <div className="inline-flex items-center space-x-1">
                              {item.status?.toUpperCase() === 'APPLIED' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={async () => {
                                      try {
                                        await leaveService.applyLeave(orgId, selectedUserId, {
                                          ...item,
                                          status: 'approved',
                                        });
                                        setSuccessMessage(`Leave #${item.leave_id} marked as APPROVED.`);
                                        await fetchLeaveData(true);
                                      } catch (err) {
                                        setErrorMessage(err.userMessage || 'Failed to update status.');
                                      }
                                    }}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs text-rose-600 border-rose-300 hover:bg-rose-50"
                                    onClick={async () => {
                                      try {
                                        await leaveService.applyLeave(orgId, selectedUserId, {
                                          ...item,
                                          status: 'rejected',
                                        });
                                        setSuccessMessage(`Leave #${item.leave_id} marked as REJECTED.`);
                                        await fetchLeaveData(true);
                                      } catch (err) {
                                        setErrorMessage(err.userMessage || 'Failed to reject leave.');
                                      }
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              {item.status?.toUpperCase() === 'APPROVED' && (
                                <Badge variant="outline" className="text-xs text-gray-400 border-gray-200">
                                  Approved
                                </Badge>
                              )}
                              {item.status?.toUpperCase() === 'REJECTED' && (
                                <span className="text-xs text-gray-400 italic">No quota consumed</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: APPLY FOR LEAVE */}
        <TabsContent value="apply" className="space-y-4">
          <Card className="max-w-3xl mx-auto shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Send className="w-5 h-5 mr-2 text-indigo-600" />
                Submit Leave Application
              </CardTitle>
              <CardDescription>
                Endpoint: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-indigo-600">POST /{orgId}/{selectedUserId}/leave/apply</code>
                <br />
                Submits leave for Employee #{selectedUserId} under Organization #{orgId}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {applyError && (
                <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-lg flex items-center text-sm">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 text-rose-600" />
                  <span>{applyError}</span>
                </div>
              )}

              <form onSubmit={handleApplyLeave} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Leave Type Select */}
                  <div className="space-y-1.5">
                    <Label htmlFor="applyType" className="font-semibold text-xs text-gray-700">
                      Select Leave Type <span className="text-rose-500">*</span>
                    </Label>
                    <select
                      id="applyType"
                      value={applyForm.leaveTypeId}
                      onChange={(e) => setApplyForm((p) => ({ ...p, leaveTypeId: e.target.value }))}
                      required
                      className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">-- Choose Leave Type --</option>
                      {leaveBalances.map((b) => (
                        <option key={b.leave_type_id} value={b.leave_type_id}>
                          {b.leave_type} ({b.remaining} days left of {b.entitled})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status Selection (defaults to applied, admins can set others) */}
                  <div className="space-y-1.5">
                    <Label htmlFor="applyStatus" className="font-semibold text-xs text-gray-700">
                      Application Status <span className="text-rose-500">*</span>
                    </Label>
                    <select
                      id="applyStatus"
                      value={applyForm.status}
                      onChange={(e) => setApplyForm((p) => ({ ...p, status: e.target.value }))}
                      required
                      className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="applied">Applied (Standard workflow)</option>
                      <option value="approved">Approved (Pre-approved)</option>
                      <option value="on-hold">On-Hold</option>
                      <option value="rejected">Rejected (Test rejection)</option>
                    </select>
                  </div>

                  {/* From Date */}
                  <div className="space-y-1.5">
                    <Label htmlFor="applyFrom" className="font-semibold text-xs text-gray-700">
                      From Date (Start) <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="applyFrom"
                      type="date"
                      value={applyForm.fromDate}
                      onChange={(e) => handleDateChange('fromDate', e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>

                  {/* To Date */}
                  <div className="space-y-1.5">
                    <Label htmlFor="applyTo" className="font-semibold text-xs text-gray-700">
                      To Date (End) <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="applyTo"
                      type="date"
                      value={applyForm.toDate}
                      onChange={(e) => handleDateChange('toDate', e.target.value)}
                      required
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Applied Leave Days */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div className="space-y-1.5">
                    <Label htmlFor="applyDays" className="font-semibold text-xs text-gray-700">
                      Number of Leave Days <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="applyDays"
                      type="number"
                      min="1"
                      value={applyForm.appliedLeave}
                      onChange={(e) => setApplyForm((p) => ({ ...p, appliedLeave: Number(e.target.value) || 1 }))}
                      required
                      className="h-10"
                    />
                    <p className="text-xs text-gray-500">Auto-calculated from date range, can be adjusted for half days.</p>
                  </div>

                  {/* Balance Impact Preview */}
                  {currentApplyTypeBalance && (
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs space-y-1">
                      <p className="font-medium text-gray-800">
                        Balance Preview for {currentApplyTypeBalance.leave_type}:
                      </p>
                      <div className="flex justify-between text-gray-600">
                        <span>Current Available:</span>
                        <span className="font-semibold">{currentApplyTypeBalance.remaining} days</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>After Application:</span>
                        <span
                          className={`font-semibold ${
                            currentApplyTypeBalance.remaining - applyForm.appliedLeave < 0
                              ? 'text-rose-600 font-bold'
                              : 'text-emerald-700'
                          }`}
                        >
                          {currentApplyTypeBalance.remaining - applyForm.appliedLeave} days
                        </span>
                      </div>
                      {currentApplyTypeBalance.remaining - applyForm.appliedLeave < 0 && (
                        <p className="text-rose-600 font-semibold pt-1 flex items-center">
                          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                          Exceeds entitlement quota! Backend will return 409 Conflict.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Reason (Optional) */}
                <div className="space-y-1.5">
                  <Label htmlFor="applyReason" className="font-semibold text-xs text-gray-700">
                    Reason / Description (Optional)
                  </Label>
                  <Input
                    id="applyReason"
                    placeholder="e.g., Medical recovery, family commitment, vacation"
                    value={applyForm.reason}
                    onChange={(e) => setApplyForm((p) => ({ ...p, reason: e.target.value }))}
                    className="h-10"
                  />
                </div>

                <div className="pt-2 flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setActiveTab('overview')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={applySubmitting}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-32"
                  >
                    {applySubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Apply Leave'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: ORG LEAVE TYPES & QUOTAS */}
        <TabsContent value="org-policies" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create Leave Type Form */}
            <Card className="lg:col-span-1 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Plus className="w-4 h-4 mr-2 text-indigo-600" />
                  Add Org Leave Type
                </CardTitle>
                <CardDescription>
                  Endpoint: <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">POST /{orgId}/leave-types</code>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {createTypeError && (
                  <div className="mb-3 p-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded text-xs flex items-center">
                    <AlertCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                    {createTypeError}
                  </div>
                )}
                <form onSubmit={handleCreateLeaveType} className="space-y-3">
                  <div>
                    <Label htmlFor="typeName" className="text-xs font-semibold">
                      Leave Type Name <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="typeName"
                      placeholder="e.g., Sick Leave, Casual Leave, Maternity Leave"
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                      required
                      className="mt-1 h-9 text-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Duplicate leave type names in the same org will return 400 Bad Request.
                    </p>
                  </div>
                  <Button
                    type="submit"
                    disabled={createTypeLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-9"
                  >
                    {createTypeLoading ? 'Creating...' : 'Create Leave Type'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* List of Org Leave Types & Org Default Quotas */}
            <Card className="lg:col-span-2 shadow-sm">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Organization Leave Types & Default Quotas</CardTitle>
                  <CardDescription>
                    Org #{orgId} &bull; Endpoints: <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">GET /{orgId}/leave-types</code> and <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">POST /{orgId}/&#123;leaveTypeId&#125;</code>
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead className="w-20">Type ID</TableHead>
                        <TableHead>Leave Type</TableHead>
                        <TableHead>Org ID</TableHead>
                        <TableHead className="text-center">Org Default Days</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaveTypes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-6 text-gray-500 text-xs">
                            No leave types created for this organization yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        leaveTypes.map((type) => {
                          const orgDefault = orgLeaveCounts[type.leave_type_id] ?? 0;
                          return (
                            <TableRow key={type.leave_type_id} className="hover:bg-slate-50/70">
                              <TableCell className="font-mono text-xs font-semibold">
                                #{type.leave_type_id}
                              </TableCell>
                              <TableCell className="font-semibold text-gray-900">
                                {type.leave_type}
                              </TableCell>
                              <TableCell className="text-xs text-gray-600">
                                Org #{type.org_id}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="secondary" className="font-mono text-xs font-bold">
                                  {orgDefault} days
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                                  Active
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openSetQuota('ORG', type)}
                                  className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                >
                                  <Sliders className="w-3 h-3 mr-1" /> Set Default Quota
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 4: EMPLOYEE QUOTAS */}
        <TabsContent value="employee-entitlements" className="space-y-4">
          <Card>
            <CardHeader className="pb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <CardTitle className="text-lg">Employee Leave Entitlements Configuration</CardTitle>
                <CardDescription>
                  Managing quotas for Employee #{selectedUserId} &bull; Endpoints: <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">GET /{orgId}/{selectedUserId}</code> &bull; <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">POST /{orgId}/{selectedUserId}/&#123;leaveTypeId&#125;</code>
                </CardDescription>
              </div>

              {/* Sync Org Defaults button */}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await Promise.all(
                      leaveTypes.map(async (t) => {
                        const defaultCount = orgLeaveCounts[t.leave_type_id] || 12;
                        await leaveService.setEmployeeLeaveCount(orgId, selectedUserId, t.leave_type_id, defaultCount);
                      })
                    );
                    setSuccessMessage(`Assigned org defaults to Employee #${selectedUserId}.`);
                    await fetchLeaveData(true);
                  } catch (err) {
                    setErrorMessage(err.userMessage || 'Failed to sync defaults.');
                  }
                }}
                className="text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              >
                <Sliders className="w-3.5 h-3.5 mr-1" />
                Assign Org Defaults to This Employee
              </Button>
            </CardHeader>

            <CardContent>
              <div className="border rounded-xl overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-24">Leave Type ID</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Employee ID</TableHead>
                      <TableHead className="text-center">Org Default</TableHead>
                      <TableHead className="text-center">Allocated Entitlement</TableHead>
                      <TableHead className="text-center">Remaining Balance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveBalances.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6 text-gray-500 text-xs">
                          No leave quotas configured for this employee yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      leaveBalances.map((item) => (
                        <TableRow key={item.leave_type_id} className="hover:bg-slate-50/70">
                          <TableCell className="font-mono text-xs font-semibold">
                            #{item.leave_type_id}
                          </TableCell>
                          <TableCell className="font-semibold text-gray-900">
                            {item.leave_type}
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">
                            User #{item.user_id || selectedUserId}
                          </TableCell>
                          <TableCell className="text-center text-xs text-gray-500">
                            {item.orgDefault} days
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-mono text-sm font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                              {item.entitled} days
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`text-xs font-bold px-2 py-0.5 rounded ${
                                item.remaining === 0
                                  ? 'bg-rose-100 text-rose-800'
                                  : item.remaining <= 3
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {item.remaining} left ({item.used} used)
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openSetQuota('EMPLOYEE', item)}
                              className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                            >
                              <Sliders className="w-3 h-3 mr-1" /> Edit Entitlement
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 5: QA FLOW & API TESTER */}
        <TabsContent value="qa-tester" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center">
                  <PlayCircle className="w-5 h-5 mr-2 text-rose-600" />
                  Suggested QA Flow Runner (15 API Steps)
                </CardTitle>
                <CardDescription>
                  Directly tests and verifies all 15 scenarios from pages 6-7 of the Leave Management API Spec.
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={runQaFlow}
                  disabled={qaRunning}
                  className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs h-9 shadow-md shadow-rose-600/20"
                >
                  {qaRunning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Running 15 Steps...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Run All 15 QA Steps
                    </>
                  )}
                </Button>
                {qaLogs.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQaLogs([])}
                    className="text-xs h-9"
                  >
                    Clear Logs
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto max-h-[500px] overflow-y-auto border border-slate-800 space-y-3">
                {qaLogs.length === 0 ? (
                  <div className="text-slate-400 py-6 text-center">
                    <p className="font-semibold text-sm text-slate-300">No QA tests run yet.</p>
                    <p className="text-xs mt-1">Click "Run All 15 QA Steps" above to execute all 15 scenarios from the spec.</p>
                  </div>
                ) : (
                  qaLogs.map((log) => (
                    <div
                      key={log.stepNumber}
                      className={`p-3 rounded-lg border ${
                        log.status === 'PASS'
                          ? 'bg-emerald-950/40 border-emerald-800/60 text-emerald-200'
                          : 'bg-rose-950/40 border-rose-800/60 text-rose-200'
                      }`}
                    >
                      <div className="flex items-center justify-between font-bold">
                        <span>
                          Step {log.stepNumber}: {log.title}
                        </span>
                        <Badge
                          className={
                            log.status === 'PASS'
                              ? 'bg-emerald-600 text-white text-[10px]'
                              : 'bg-rose-600 text-white text-[10px]'
                          }
                        >
                          {log.status}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-slate-300 mt-1">{log.details}</p>
                      {log.payload && (
                        <pre className="mt-2 p-2 bg-slate-950 rounded text-[10px] text-indigo-300 overflow-x-auto">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quota Modal (For Org Quota or Employee Quota) */}
      <Dialog open={quotaModal.isOpen} onOpenChange={(open) => setQuotaModal((p) => ({ ...p, isOpen: open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {quotaModal.mode === 'ORG' ? 'Set Organization Default Quota' : 'Set Employee Leave Quota'}
            </DialogTitle>
            <DialogDescription>
              {quotaModal.mode === 'ORG' ? (
                <>
                  Endpoint: <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">POST /{orgId}/{quotaModal.leaveTypeId}</code>
                  <br />
                  Updates default leave days for "{quotaModal.leaveTypeName}" in Org #{orgId}.
                </>
              ) : (
                <>
                  Endpoint: <code className="text-xs bg-slate-100 px-1 rounded text-indigo-600">POST /{orgId}/{quotaModal.targetUserId}/{quotaModal.leaveTypeId}</code>
                  <br />
                  Updates allocated leave days for Employee #{quotaModal.targetUserId} for "{quotaModal.leaveTypeName}".
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {quotaError && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded text-xs flex items-center">
              <AlertCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
              {quotaError}
            </div>
          )}

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-gray-500">Leave Type</Label>
              <div className="font-semibold text-gray-900 mt-0.5">{quotaModal.leaveTypeName}</div>
            </div>

            <div>
              <Label htmlFor="newQuotaCount" className="text-xs font-semibold">
                Total Allowed Leave Days <span className="text-rose-500">*</span>
              </Label>
              <Input
                id="newQuotaCount"
                type="number"
                min="0"
                value={quotaModal.newCount}
                onChange={(e) => setQuotaModal((p) => ({ ...p, newCount: Number(e.target.value) }))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Must be greater than or equal to 0.</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setQuotaModal((p) => ({ ...p, isOpen: false }))}
              disabled={quotaSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveQuota}
              disabled={quotaSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {quotaSubmitting ? 'Saving...' : 'Save Quota'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leaves;
